import { useCallback, useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import useQueryParams from 'hooks/useQueryParams';
import type { SearchableFormValues } from 'components/SearchableFilter';

import * as ManagementLayout from 'components/ManagementLayout';

import { useCurrentUserContext } from 'utils/User';

import { TabButton } from './components/TabButton';

import OemSegments from './OemSegments';
import Teams from './Teams';
import Fleets from './Fleets';
import Depots from './Depots';

import { ManagementPageContext } from './contexts/ManagementPageContext';

type TabKey = 'oem-segments' | 'teams' | 'accounts' | 'depots';
type TabObject = Record<TabKey, { label: string; value: React.ComponentType }>;

export interface ManagementQueryParams {
  tab: TabKey | undefined;
  oemSegmentIds: string[];
  teamIds: string[];
  accountIds: string[];
  depotIds: string[];
}

function getTabObject(tabKey: TabKey) {
  const TabObjects: TabObject = {
    'oem-segments': { label: t`OEM Segments`, value: OemSegments },
    teams: { label: t`Teams`, value: Teams },
    accounts: { label: t`Accounts`, value: Fleets },
    depots: { label: t`Depots`, value: Depots },
  };

  return TabObjects[tabKey];
}

interface GenerateInitialTabParams {
  showOemSegmentsTab: boolean;
  showTeamsTab: boolean;
}

function generateInitialTab({
  showOemSegmentsTab,
  showTeamsTab,
}: GenerateInitialTabParams) {
  if (showOemSegmentsTab) return 'oem-segments';
  if (showTeamsTab) return 'teams';

  return 'accounts';
}

const ManagementPage = () => {
  const { isSuperUser, isOemManager, oemSegments, managedSentinelTeams } =
    useCurrentUserContext();

  /* istanbul ignore next -- @preserve */
  const oemSegmentsLength = oemSegments?.length ?? 0;
  const hasMultipleOemSegments = isOemManager && oemSegmentsLength > 1;
  /* istanbul ignore next -- @preserve */
  const showOemSegmentsTab = (isSuperUser || hasMultipleOemSegments) ?? false;

  /* istanbul ignore next -- @preserve */
  const amountOfManagedTeams = managedSentinelTeams?.length ?? 0;
  const showTeamsTab = isSuperUser || amountOfManagedTeams > 1;
  const initialTab = generateInitialTab({ showOemSegmentsTab, showTeamsTab });

  const [pageParams, setPageParams] = useQueryParams<ManagementQueryParams>(
    'management-page',
    {
      tab: initialTab,
      oemSegmentIds: [],
      teamIds: [],
      accountIds: [],
      depotIds: [],
    },
    {
      // Type coercion is disabled to prevent automatic conversion of array params to integers
      // This ensures the IDs remain as strings as they GraphQL types.
      typeCoercion: false,
    },
  );

  /* istanbul ignore next -- @preserve */
  const selectedTab = pageParams.tab || initialTab;

  const handleTabChange = useCallback(
    (tab: TabKey) => () => {
      setPageParams((oldState) => ({ ...oldState, tab }));
    },
    [setPageParams],
  );

  const { label, value: SelectedTabComponent } = useMemo(
    () => getTabObject(selectedTab),
    [selectedTab],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeOemSegmentsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        oemSegmentIds: value as string[],
        teamIds: [],
        accountIds: [],
        depotIds: [],
      }));
    },
    [setPageParams],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeTeamsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        teamIds: value as string[],
        accountIds: [],
        depotIds: [],
      }));
    },
    [setPageParams],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeAccountsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        accountIds: value as string[],
        depotIds: [],
      }));
    },
    [setPageParams],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeDepotsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        depotIds: value as string[],
      }));
    },
    [setPageParams],
  );

  const values = useMemo(
    () => ({
      pageParams,
      setPageParams,
      handleChangeOemSegmentsFilter,
      handleChangeTeamsFilter,
      handleChangeAccountsFilter,
      handleChangeDepotsFilter,
    }),
    [
      pageParams,
      setPageParams,
      handleChangeOemSegmentsFilter,
      handleChangeTeamsFilter,
      handleChangeAccountsFilter,
      handleChangeDepotsFilter,
    ],
  );

  return (
    <ManagementPageContext.Provider value={values}>
      <ManagementLayout.Root>
        <ManagementLayout.Header.Container>
          <ManagementLayout.Header.Left>
            <ManagementLayout.Header.Title>
              <Trans>Management</Trans> / {label}
            </ManagementLayout.Header.Title>
          </ManagementLayout.Header.Left>
        </ManagementLayout.Header.Container>
        <ManagementLayout.EntityTabsContainer>
          {showOemSegmentsTab && (
            <TabButton
              text={t`OEM Segments`}
              onClick={handleTabChange('oem-segments')}
              active={selectedTab === 'oem-segments'}
            />
          )}
          {showTeamsTab && (
            <TabButton
              text={t`Teams`}
              onClick={handleTabChange('teams')}
              active={selectedTab === 'teams'}
            />
          )}
          <TabButton
            text={t`Accounts`}
            onClick={handleTabChange('accounts')}
            active={selectedTab === 'accounts'}
          />
          <TabButton
            text={t`Depots`}
            onClick={handleTabChange('depots')}
            active={selectedTab === 'depots'}
          />
        </ManagementLayout.EntityTabsContainer>
        <SelectedTabComponent />
      </ManagementLayout.Root>
    </ManagementPageContext.Provider>
  );
};

export default ManagementPage;
